import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ShieldCheck, HardHat, ClipboardCheck, HeartPulse, ArrowRight } from 'lucide-react';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { IsnBadge } from '../components/branding/TrustBadges';

const COMMITMENTS = [
  {
    icon: ClipboardCheck,
    title: 'Planning With Purpose',
    desc: 'Successful projects begin long before construction starts. Through detailed preplanning, job hazard analyses, daily pre-task meetings, and site-specific execution plans, our teams identify and control risk before work begins. Thoughtful planning creates safer jobsites, greater efficiency, and more predictable project outcomes.',
  },
  {
    icon: HardHat,
    title: 'Prepared Professionals',
    desc: 'Our people are our greatest strength. Every member of the OMC team is trained, qualified, and equipped to perform specialized work safely and professionally. Ongoing education, field leadership, and personal accountability ensure every project is executed to the highest standards of safety and craftsmanship.',
  },
  {
    icon: HeartPulse,
    title: 'Protecting What Matters Most',
    desc: 'The well-being of our employees, trade partners, clients, and the communities where we work will always come first. Every decision in the field is guided by the belief that no deadline, schedule, or production goal is more important than sending everyone home safely at the end of the day.',
  },
  {
    icon: ShieldCheck,
    title: 'Excellence Without Compromise',
    desc: 'A strong safety culture is built through consistency, not coincidence. Our record of safe project delivery reflects decades of disciplined execution, continuous improvement, and an unwavering commitment to doing the work the right way, every time.',
  },
];

const STATS = [
  { v: '0', l: 'Compromise on Safety' },
  { v: '100%', l: 'Trained & Qualified Crews' },
  { v: '25+', l: 'Years Building Safely' },
];

export default function SafetyPage() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="safety-page">
      <Navbar />
      <main id="main-content" tabIndex={-1}>

      {/* Hero band */}
      <section className="relative bg-coal text-bone overflow-hidden pt-20 sm:pt-24 pb-20 sm:pb-24 border-b-4 border-[hsl(var(--forest))]">
        <div className="absolute inset-0 bg-grain opacity-[0.05] pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 50% at 20% 20%, hsl(80 57% 31% / 0.14) 0%, transparent 70%)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="kicker kicker-on-dark mb-4" data-testid="safety-kicker">Safety</p>
          <h1 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05] mb-6" data-testid="safety-headline">
            Safety Is a
            <span className="italic font-bold text-forest-2"> Standard.</span>
          </h1>
          <p className="text-bone/75 leading-[1.75] text-[15px] sm:text-lg max-w-2xl">
            Safety is fundamental to the way we build. It is embedded in every decision,
            every plan, and every phase of construction. From preconstruction through final
            completion, our commitment to protecting people is as uncompromising as our
            commitment to quality.
          </p>
        </div>
      </section>

      {/* Commitments */}
      <section ref={ref} className="relative py-20 sm:py-24 bg-background overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-[0.025] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >
            <p className="kicker">Our Commitment to Safety</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {COMMITMENTS.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 18 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                  className="rounded-xl border border-border bg-background p-7 shadow-elev-1"
                  data-testid={`safety-commitment-${i}`}
                >
                  <span className="w-12 h-12 rounded-lg bg-[hsl(var(--forest)/0.10)] border border-[hsl(var(--forest)/0.28)] flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[hsl(var(--forest))]" strokeWidth={1.6} />
                  </span>
                  <h3 className="font-display font-semibold text-xl text-foreground mb-2.5 leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-[14px] text-foreground/70 leading-[1.7]">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* ISNetworld prequalification badge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-16 flex flex-col items-center text-center"
            data-testid="safety-isn-badge"
          >
            <div className="text-[hsl(var(--forest))]">
              <IsnBadge size={116} />
            </div>
            <h3 className="mt-6 font-display font-semibold text-xl text-foreground">
              ISNetworld Member Contractor
            </h3>
            <p className="mt-2 text-[14px] text-foreground/65 leading-[1.7] max-w-md">
              Prequalified and continuously verified for safety, compliance, and insurance
              standards through ISNetworld.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stat strip + CTA */}
      <section className="relative bg-coal text-bone py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-[0.05] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {STATS.map((s) => (
              <div key={s.l} className="text-center sm:text-left">
                <p className="editorial-headline text-4xl sm:text-5xl text-bone leading-none mb-2">{s.v}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-bone/55">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 border-t border-white/10 pt-10">
            <p className="text-bone/70 text-[15px] max-w-xl leading-relaxed">
              Building something complex and want a partner who takes safety as seriously as
              you do? Let&rsquo;s talk about your project.
            </p>
            <Button
              onClick={() => navigate('/contact')}
              className="btn-lift bg-primary hover:bg-[hsl(var(--water-blue-3))] gap-2 rounded-xl px-6 shrink-0"
              data-testid="safety-contact-btn"
            >
              Discuss Your Project
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
